import HttpService from "common/http/httpService";
import ReturnsNullOn404 from "common/http/returnsNullOn404";
import ThrowsValidationError from "common/http/throwsValidationError";
import { CustomField } from "domain/customField";
import { ErpIntegrationThin } from "domain/erpIntegration";
import { Fee } from "domain/fee";
import Merchant from "domain/merchant";
import { MerchantLink, stripStatus } from "domain/merchantLink";
import activeMerchantService from "./activeMerchantService";
import PaymentOptionDetails from "domain/paymentOptionDetails";
import { Tag } from "domain/tag";
import Throttle from "common/decorators/throttle";
import FormFieldSetting, { FormField } from "domain/formFieldSetting";
import ContractExtendedType from "domain/contractExtendedType";

class MerchantService extends HttpService {

    @ThrowsValidationError()
    async updateMerchant(request: UpdateMerchantRequest): Promise<void> {
        await this.getClient().put(`/settings/v1/merchants/${activeMerchantService.getActiveMerchant()}`, request);
    }

    @ThrowsValidationError()
    async getMerchantLinks(): Promise<MerchantLink[]> {
        const result = await this.getClient().get<MerchantLink[]>('/settings/v1/merchant-links');
        return result.data;
    }

    @ThrowsValidationError()
    async upsertMerchantLink(link: MerchantLink): Promise<MerchantLink> {
        const linkWithoutStatus = stripStatus(link);
        const result = await this.getClient().put('/settings/v1/merchant-links', linkWithoutStatus);
        return result.data;
    }

    async deleteMerchantLink(linkId: number): Promise<void> {
        await this.getClient().delete(`/settings/v1/merchant-links/${linkId}`);
    }

    @ThrowsValidationError()
    async upsertFee(request: Fee): Promise<Fee> {
        const result = await this.getClient().put('/offers/v1/merchants/fee', request);
        return result.data;
    }

    async deleteFees(feeId: string): Promise<void> {
        await this.getClient().delete(`/offers/v1/merchants/fees/${feeId}`);
    }

    async listAllMerchants(): Promise<Merchant[]> {
        const result = await this.getClient().get<Merchant[]>('/settings/v1/merchants');
        return result.data;
    }

    @Throttle(100, true)
    async listFees(): Promise<Fee[]> {
        const result = await this.getClient().get<Fee[]>('/offers/v1/merchants/fees');
        return result.data;
    }

    @Throttle(1000, true)
    async listCustomFields(): Promise<CustomField[]> {
        const result = await this.getClient().get<CustomField[]>('/offers/v1/merchants/custom-fields');
        return result.data;
    }

    async listTags(): Promise<Tag[]> {
        const result = await this.getClient().get<Tag[]>('/settings/v1/merchants/tags');
        return result.data;
    }

    @ReturnsNullOn404()
    async getErpIntegration(): Promise<ErpIntegrationThin> {
        const response = await this.getClient().get<ErpIntegrationThin>('/offers/v1/merchants/erp-integration');
        return response.data;
    }

    async syncErpContracts(request: SyncErpContractRequest): Promise<void> {
        await this.getClient().post(`/offers/v1/merchants/erp-integration/sync-erp-contracts`, request);
    }

    async listPaymentOptions(): Promise<PaymentOptionDetails[]> {
        const response = await this.getClient().get<PaymentOptionDetails[]>('/settings/v1/merchants/payment-options');
        return response.data;
    }

    async listFormFieldSettings(): Promise<FormFieldSetting[]> {
        const response = await this.getClient().get<FormFieldSetting[]>('/settings/v1/merchants/form-field-settings');
        return response.data;
    }

    async listEditableContractFields(): Promise<FormField[]> {
        const response = await this.getClient().get<FormField[]>('/settings/v1/merchants/editable-contract-fields');
        return response.data;
    }

    async upsertFormField(formFieldSetting: FormFieldSetting): Promise<void> {
        await this.getClient().put(`/settings/v1/merchants/form-field-settings/${formFieldSetting.formField}`, formFieldSetting);
    }

    async listContractExtendedTypes(): Promise<ContractExtendedType[]> {
        const response = await this.getClient().get<ContractExtendedType[]>("/offers/v1/merchants/contract-extended-types");
        return response.data;
    }
}

interface UpdateMerchantRequest {
    defaultDeferPayInterestRate?: number;
    displayDeliveryTermsWhenCustomerWillBuy?: boolean;
    displayDeliveryTermsWhenCustomerWillSell?: boolean;
    isPriceAtOpenEnabled?: boolean;
    sendNotificationToCustomerDefaultValue?: boolean;
    restrictLocationsOnContractsToMappings?: boolean;
    enableHedgeSymbolOverrides?: boolean;
}

interface SyncErpContractRequest {
    isPurchase: boolean;
}

const merchantService = new MerchantService();
export default merchantService;